import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lettersForMutations, mutationTypes } from './mutationUtils';
import { Card, CardContent, CardHeader, Collapse, FormControlLabel, Grid, IconButton, Switch } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { lowercaseMixedMutations } from './mixedMutations';
import { lowercaseHardMutations } from './hardMutations';
import { lowercaseSoftMutations } from './softMutations';
import { lowercaseSpiranteMutations } from './spiranteMutations';
import { lowercaseIncompleteMutations } from './incompleteMutations';
import clsx from 'clsx';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BtnOpenMutation from './BtnOpenMutation';

const useStyles = makeStyles((theme) => ({
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
}));


const spiranteMutations = {
  id: 'spirante',
  type: mutationTypes.SPIRANTE,
  mutations: {
    ...lowercaseSpiranteMutations,
  },
  shouldShow: true,
};

const hardMutations = {
  id: 'hard',
  type: mutationTypes.HARD,
  mutations: {
    ...lowercaseHardMutations,
  },
  shouldShow: true,
};

const mixedMutations = {
  id: 'mixed',
  type: mutationTypes.MIXED,
  mutations: {
    ...lowercaseMixedMutations,
  },
  shouldShow: true,
};

const softMutations = {
  id: 'soft',
  type: mutationTypes.SOFT,
  mutations: {
    ...lowercaseSoftMutations,
  },
  shouldShow: true,
};

const softIncompleteMutations = {
  id: 'softIncomplete',
  type: mutationTypes.SOFT_INCOMPLETE,
  mutations: {
    'k': 'g',
    'p': 'b',
    't': 'd',
    'g': 'c\'h',
    'gw': 'w',
    'b': 'v',
    'm': 'v',
  },
  shouldShow: false,
};

const softIncompleteConditionalMutations = {
  id: 'softIncompleteConditional',
  type: mutationTypes.SOFT_CONDITIONAL,
  mutations: {
    'k': '(-lnmrv aeiou) g',
    'p': '(-lnmrv aeiou) b',
    't': '(-lnmrv aeiou) d',
    'g': 'c\'h',
    'gw': 'w',
    'b': 'v',
    'd': 'z',
    'm': 'v',
  },
  shouldShow: false,
};

const incompleteMutations = {
  id: 'incomplete',
  type: mutationTypes.INCOMPLETE,
  mutations: {
    ...lowercaseIncompleteMutations,
  },
  shouldShow: false,
};

const MutationsOverview = () => {

  const { t } = useTranslation();

  const headers = {
    [ spiranteMutations.id ]: t('overview_spirante'),
    [ hardMutations.id ]: t('overview_hard'),
    [ mixedMutations.id ]: t('overview_mixed'),
    [ softMutations.id ]: t('overview_soft'),
    [ softIncompleteMutations.id ]: t('overview_subs_f_sg_m_pl'),
    [ incompleteMutations.id ]: t('overview_subs_m_sg_f_pl'),
    [ softIncompleteConditionalMutations.id ]: t('overview_adj_f_sg_m_pl'),
  };

  const allMutations = [
    spiranteMutations,
    hardMutations,
    mixedMutations,
    softMutations,
    softIncompleteMutations,
    incompleteMutations,
    softIncompleteConditionalMutations,
  ];

  const [ htmlMutations, setMutations ] = useState([
    spiranteMutations,
    hardMutations,
    mixedMutations,
    softMutations,
    softIncompleteMutations,
    incompleteMutations,
    softIncompleteConditionalMutations,
  ]);

  const [ isModeCompact, setModeCompact ] = useState(false);

  function handleInputChange (event) {
    const target = event.target;

    if (target.name.startsWith('mutation-')) {
      const mutationId = target.value;
      const mutation = htmlMutations.find(mutation => mutation.id === mutationId);
      mutation.shouldShow = !mutation.shouldShow;
      setMutations([ ...htmlMutations ]);

    } else if (target.name === 'mode-compact') {
      setModeCompact(!isModeCompact);
    }
  }

  const classes = useStyles();
  const [ isExpanded, setExpanded ] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!isExpanded);
  };

  return (
    <Card>
      <CardHeader
        title={'All'}
        action={
          <>
            <BtnOpenMutation mutationType={mutationTypes.SPIRANTE} />
            <BtnOpenMutation mutationType={mutationTypes.HARD} />
            <BtnOpenMutation mutationType={mutationTypes.MIXED} />
            <BtnOpenMutation mutationType={mutationTypes.SOFT} />
            <BtnOpenMutation mutationType={mutationTypes.INCOMPLETE} />
            <BtnOpenMutation mutationType={mutationTypes.SOFT_INCOMPLETE} />
            <BtnOpenMutation mutationType={mutationTypes.SOFT_CONDITIONAL} />
            <IconButton
              className={clsx(classes.expand, {
                [ classes.expandOpen ]: isExpanded,
              })}
              onClick={handleExpandClick}
              aria-expanded={isExpanded}
              aria-label="show more"
              size="large">
              <ExpandMoreIcon />
            </IconButton>
          </>
        } />
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <CardContent>
          <Grid container spacing={3} className={'overview-with-controls'}>
            <Grid item xs={12} sm={6} className={'table-container'}>
              <table>
                <thead>
                <tr>
                  {/*<th></th>*/}
                  {htmlMutations.filter(htmlMutation => htmlMutation.shouldShow)
                    .map(htmlMutation => (<th key={htmlMutation.id}>
                        <div className={`ov-header ${isModeCompact ? 'ov-header__compact' : ''}`}>
                  <span className={'mutation-label ' + htmlMutation.type}
                        dangerouslySetInnerHTML={{ __html: headers[ htmlMutation.id ] }}></span>
                        </div>
                      </th>),
                    )}
                </tr>
                </thead>
                <tbody>
                {lettersForMutations
                  .map(letter => (
                    <tr key={letter} className={'ov-row'}>
                      {htmlMutations.filter(htmlMutation => htmlMutation.shouldShow)
                        .map(htmlMutation => {

                          const mutatedLetter = htmlMutation.mutations[ letter ];
                          const mutationLabel = mutatedLetter ? `<em>${letter} → </em>${mutatedLetter}` : '';

                          return (<td key={htmlMutation.id}>
                            <div className={`mutation-bg ${htmlMutation.type} ${mutationLabel ? 'has-value' : ''}`}>
                              <span dangerouslySetInnerHTML={{ __html: mutationLabel }}></span></div>
                          </td>);
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={'overview-controls'}>
                <form className={'overview-controls-form'}>
                  <FormControlLabel
                    control={<Switch
                      checked={isModeCompact}
                      onChange={event => handleInputChange(event)}
                      name="mode-compact"
                    />}
                    label={t('overview_mode-compact')}
                  />

                  {allMutations.map(htmlMutation => (
                    <FormControlLabel
                      key={htmlMutation.id}
                      control={<Switch
                        checked={htmlMutation.shouldShow}
                        onChange={event => handleInputChange(event)}
                        name={'mutation-' + htmlMutation.id}
                        value={htmlMutation.id}
                      />}
                      label={<span dangerouslySetInnerHTML={{ __html: headers[ htmlMutation.id ] }}></span>}
                    />
                  ))}
                </form>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default memo(MutationsOverview);
